<template>
  <div class="hidden"></div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { languageMap } from '~/models/locale'
import { type CompanyRequisites, Currency } from '~/models/orders'
import { useAuth } from '~/stores/auth'

const { locale } = useI18n()
const authStore = useAuth()

const emit = defineEmits(['close', 'done'])

interface CloudPaymentsParams {
  amount: number
  invoiceId: string
  description?: string // event name
  receiptLabel?: string
  skin?: string
  autoClose?: number
  currency?: Currency
  configuration?: {
    common?: {
      successRedirectUrl: string
      failRedirectUrl: string
    }
    paymentMethodsOrder?: any[]
  }
  payer?: {
    firstName?: string
    lastName?: string
    middleName?: string
    birth?: string
    address?: string
    street?: string
    city?: string
    country?: string
    phone?: string
    postcode?: string
  }
}

interface CloudPaymentsRecurrentParams {
  interval: 'Day' | 'Week' | 'Month'
  period: number
}

async function processCloudPayments(
  config: CloudPaymentsParams,
  companyRequisites?: CompanyRequisites,
  recurrentData?: CloudPaymentsRecurrentParams
) {
  if (!window.cp?.CloudPayments) {
    await new Promise((resolve, reject) => {
      try {
        const s = document.createElement('script')
        s.type = 'text/javascript'
        s.async = true
        s.src = 'https://widget.cloudpayments.ru/bundles/cloudpayments.js'
        s.addEventListener(
          'load',
          () => {
            if (!window.cp?.CloudPayments) {
              reject(new Error('CloudPayments is not defined'))
            }
            resolve(true)
          },
          false
        )
        const head = document.getElementsByTagName('head')[0]
        head.appendChild(s)
      } catch (error: any) {
        reject(error)
      }
    })
  }

  const widget = new window.cp.CloudPayments({
    language: languageMap[locale.value],
    applePaySupport: false,
    googlePaySupport: false,
    yandexPaySupport: false,
    tinkoffInstallmentSupport: false,
  })

  const publicId = useRuntimeConfig().public.cloudPaymentsId
  const amount = Number(config.amount / 100)
  const email = authStore.profile?.email

  const receipt = {
    Items: [
      {
        label:
          config.receiptLabel || 'Лицензия на использование онлайн ПО WN Hub',
        price: amount,
        quantity: 1.0,
        amount,
        vat: null, // НДС не облагается
        method: 4, // тег-1214
        object: 4, // тег-1212
      },
    ],
    calculationPlace: 'wnhub.io',
    email,
    isBso: false,
    amounts: {
      electronic: amount,
      advancePayment: 0.0,
      credit: 0.0,
      provision: 0.0,
    },
    ...(companyRequisites && {
      CustomerInfo: [
        companyRequisites.company_name,
        companyRequisites.company_address,
        `ИНН: ${companyRequisites.company_vat_id}`,
      ].join(', '),
    }),
  }

  const data = {
    CloudPayments: {
      CustomerReceipt: receipt,
      ...(recurrentData && {
        recurrent: { customerReceipt: receipt, ...recurrentData },
      }),
    },
  }

  const widgetConfig = {
    publicId,
    email,
    accountId: authStore.profile?.id,
    ...useCloneDeep(config),
    amount,
    data,
  }

  widgetConfig.receiptLabel && delete config.receiptLabel

  widgetConfig.currency ??= Currency.RUB

  widgetConfig.configuration ??= {}
  widgetConfig.configuration.paymentMethodsOrder ??= getPaymentMethodsOrder()

  console.log('CloudPayments pay with', widgetConfig)

  widget.pay('charge', widgetConfig, {
    onSuccess: function (options: any) {
      console.log('CloudPayments success', options)
      emit('done')
    },
    onFail: function (reason: any, options: any) {
      emit('close')
      console.log('CloudPayments faild', reason, options)
    },
    // onComplete: function (paymentResult: any, options: any) {
    //   console.log('CloudPayments complete', paymentResult, options)
    // },
  })
}

function getPaymentMethodsOrder() {
  if (!window?.cp?.constant?.ExternalPaymentMethods) {
    return
  }

  return [
    window.cp.constant.ExternalPaymentMethods.SbpPay,
    window.cp.constant.ExternalPaymentMethods.TinkoffPay,
    window.cp.constant.ExternalPaymentMethods.Card,
  ]
}

defineExpose({ processCloudPayments })
</script>
